<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faLampDesk,
  faHeadphones,
  faBagShopping,
  faLock,
  faUniversalAccess,
  faCopyright,
} from '@fortawesome/pro-regular-svg-icons'

import Typography from '@/components/common/Typography.vue'

interface IAppFooter {
  darkmode?: boolean
}
defineProps<IAppFooter>()
</script>

<template>
  <footer :class="{ darkmode: darkmode }">
    <CommonFeedbackButton />
    <nav>
      <ul>
        <li>
          <a
            href="https://skole.aschehoug.no/kundeservice"
            target="_blank"
            class="link"
          >
            <FontAwesomeIcon :icon="faHeadphones" />
            {{ $t('footer.service') }}
          </a>
        </li>
        <li>
          <nuxt-link
            to="/hvitlisting"
            class="link"
          >
            <FontAwesomeIcon :icon="faLampDesk" />
            {{ $t('footer.whitelist') }}
          </nuxt-link>
        </li>
        <li>
          <nuxt-link
            to="/personvern"
            class="link"
          >
            <FontAwesomeIcon :icon="faLock" />
            {{ $t('footer.privacy') }}
          </nuxt-link>
        </li>
        <li>
          <nuxt-link
            to="/universell-utforming"
            class="link"
          >
            <FontAwesomeIcon :icon="faUniversalAccess" />
            {{ $t('footer.uu') }}
          </nuxt-link>
        </li>
        <li>
          <a
            href="https://skole.aschehoug.no/"
            target="_blank"
            class="link"
          >
            <FontAwesomeIcon :icon="faBagShopping" />
            {{ $t('footer.store') }}
          </a>
        </li>
        <li>
          <nuxt-link
            to="/rettigheter"
            class="link"
          >
            <FontAwesomeIcon :icon="faCopyright" />
            {{ $t('footer.copyrights') }}
          </nuxt-link>
        </li>
      </ul>
    </nav>

    <Typography class="trademark">
      {{ `Aschehoug &copy; ${new Date().getFullYear()}` }}
    </Typography>
  </footer>
</template>

<style lang="scss" scoped>
  @include color-pairs;

  footer {
    --ks-secondary: var(--color-pair-background-color);

    color: var(--color-pair-background-color, var(--app-theme-color));
    background-color: var(--color-pair-color, var(--app-theme-background-color));
    text-align: center;
    padding: 60px 8px 40px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    @include vgs-media-tablet-and-desktop {
      padding: 96px 0 40px;
    }

    .link {
      // Safari specific color + text-decoration
      all: unset;
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }

      &:focus-visible {
        box-shadow: none;

        @include focus-ring;
      }
    }

    ul {
      list-style: none;
      display: flex;
      gap: 12px;
      align-items: center;
      justify-content: center;
      flex-flow: row wrap;
      padding: 24px;
      margin: 0 0 8px;

      @include vgs-media-mobile {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
      }

      @media screen and (width <= 580px) {
        display: flex;
        flex-direction: column;
      }

      li {
        font-size: 1.125rem; // 18px
        font-style: normal;
        font-weight: 500;
        line-height: 17px;
        display: flex;
        align-items: center;
        gap: 7px;
        padding: 12px 24px;
      }
    }

    .trademark {
      margin: 0;
    }

    .darkmode {
      background: var(--color-blue-50);
      color: var(--color-white);

      li {
        color: var(--color-white);
      }
    }
  }
</style>
